import { ComponentProps, createContext, useContext } from "react";
import { isWeb, styled, Text, Image as TGImage, useMedia, View } from "tamagui";

import { Button as ButtonBase } from "../../ui/button";
import { Card } from "../../ui/card";

interface TaskContext {
  disabled: boolean;
}
const TaskContext = createContext<TaskContext>({
  disabled: false,
});

type ContainerProps = ComponentProps<typeof Card> & {
  disabled?: boolean;
};

const useTaskContext = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTaskContext must be used within a TaskProvider");
  }
  return context;
};

const Container: React.FC<ContainerProps> = ({ disabled, ...rest }) => {
  return (
    <TaskContext.Provider value={{ disabled: !!disabled }}>
      <Card
        $sm={{
          gap: "$3",
        }}
        gap={"$4"}
        $gtSm={{
          maxWidth: 336,
        }}
        paddingHorizontal={"$3"}
        paddingVertical={"$5"}
        alignItems="center"
        backgroundColor={"$warm-snow-500"}
        shadow="md"
        {...rest}
      />
    </TaskContext.Provider>
  );
};

type ImageProps = ComponentProps<typeof TGImage> & { path?: string };

const Image: React.FC<ImageProps> = ({ path, source, ...props }) => {
  const { sm } = useMedia();
  const { disabled } = useTaskContext();

  return (
    <View
      $sm={{
        maxWidth: 100,
        maxHeight: 100,
      }}
      maxWidth={120}
      maxHeight={120}
      position="relative"
    >
      <TGImage
        {...(isWeb && {
          style: {
            maxWidth: 120,
            maxHeight: 120,
            opacity: disabled ? 0.2 : 1,
            ...(sm && {
              maxWidth: 100,
              maxHeight: 100,
            }),
          },
        })}
        source={
          path
            ? { width: sm ? 100 : 120, height: sm ? 100 : 120, uri: path }
            : source
        }
        {...props}
      />
      {disabled && (
        <View
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          backgroundColor={"$gray-300"}
          borderRadius={9999}
          opacity={0.2}
        />
      )}
    </View>
  );
};

type ButtonProps = ComponentProps<typeof ButtonBase>;

const Button: React.FC<ButtonProps> = (props) => {
  const { sm, gtSm } = useMedia();
  const { disabled } = useTaskContext();
  return (
    <ButtonBase
      variant="primary"
      size="md"
      rounded="lg"
      width={"100%"}
      {...(gtSm && { height: "$7" })}
      {...(sm && { size: "sm", maxWidth: 240 })}
      disabled={disabled}
      {...props}
    />
  );
};

const TaskTitle = styled(Text, {
  name: "TaskTitle",
  fontFamily: "$subtitle",
  color: "$gray-primary-900",
  textAlign: "center",
  disabledStyle: {
    color: "$gray-200",
  },
  variants: {
    font: {
      lg: {
        fontWeight: "$6",
        fontSize: "$6",
        lineHeight: "$6",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type TitleProps = ComponentProps<typeof TaskTitle>;

const Title: React.FC<TitleProps> = (props) => {
  const { disabled } = useTaskContext();
  return (
    <TaskTitle
      $sm={{
        fontSize: "$4",
        lineHeight: "$4",
      }}
      disabled={disabled}
      {...props}
    />
  );
};

const TaskDescription = styled(Text, {
  name: "TaskDescription",
  fontFamily: "$body",
  textAlign: "center",
  color: "$gray-primary-900",
  disabledStyle: {
    color: "$gray-200",
  },
  variants: {
    font: {
      lg: {
        fontWeight: "$4",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

type DescriptionProps = ComponentProps<typeof TaskDescription>;

const Description: React.FC<DescriptionProps> = (props) => {
  const { disabled } = useTaskContext();
  return <TaskDescription disabled={disabled} {...props} />;
};

const Disclaimer = styled(Text, {
  name: "Disclaimer",
  fontFamily: "$subtitle",
  textAlign: "center",
  color: "$gray-600",
  variants: {
    font: {
      lg: {
        fontWeight: "$4",
        fontSize: "$2",
        lineHeight: "$2",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

export const Task = {
  Container,
  Image,
  Button,
  Title,
  Description,
  Disclaimer,
};
